/** Simulation locale M8. Aucun appel externe ; les pièces viennent du jeu fictif. */
import { calculerLigne } from '@artosera/shared';
import { DEMO_FACTURES, DEMO_REGLEMENTS } from './demoFixtures.js';
import type { ApercuFec, PlanComptable } from './comptabilite.js';
import type { FichierTelecharge } from './client.js';
export const DEMO_PLAN_COMPTABLE: PlanComptable = {
  journalVentes: { code: 'VE', libelle: 'Ventes' },
  journalTresorerie: { code: 'BQ', libelle: 'Encaissements' },
  clients: { numero: '411000', libelle: 'Clients' },
  ventes: {
    normal: { numero: '707100', libelle: 'Ventes d’œuvres, TVA à 20 %' },
    reduit_5_5: { numero: '707055', libelle: 'Ventes d’œuvres, TVA à 5,5 %' },
    marge: { numero: '707300', libelle: 'Ventes d’œuvres, régime de la marge' },
    exonere: { numero: '707900', libelle: 'Ventes exonérées' },
  },
  tva: {
    normal: { numero: '445711', libelle: 'TVA collectée à 20 %' },
    reduit_5_5: { numero: '445712', libelle: 'TVA collectée à 5,5 %' },
    marge: { numero: '445713', libelle: 'TVA sur la marge' },
  },
  droitDeSuite: { numero: '467100', libelle: 'Droit de suite à reverser' },
  tresorerie: {
    virement: { numero: '512000', libelle: 'Banque' },
    carte: { numero: '512000', libelle: 'Banque' },
    cheque: { numero: '512000', libelle: 'Banque' },
    especes: { numero: '530000', libelle: 'Caisse' },
    autre: { numero: '512000', libelle: 'Banque' },
  },
};
let planDemo = structuredClone(DEMO_PLAN_COMPTABLE);
let personnalise = false;
export function lirePlanDemo() {
  return { plan: planDemo, personnalise };
}
export function ecrirePlanDemo(plan: PlanComptable) {
  planDemo = structuredClone(plan);
  personnalise = true;
  return lirePlanDemo();
}
const COLONNES = [
  'JournalCode',
  'JournalLib',
  'EcritureNum',
  'EcritureDate',
  'CompteNum',
  'CompteLib',
  'CompAuxNum',
  'CompAuxLib',
  'PieceRef',
  'PieceDate',
  'EcritureLib',
  'Debit',
  'Credit',
  'EcritureLet',
  'DateLet',
  'ValidDate',
  'Montantdevise',
  'Idevise',
];
const date = (d: string) => d.slice(0, 10).replaceAll('-', '');
const montant = (n: number) => `${Math.trunc(n / 100)},${String(n % 100).padStart(2, '0')}`;
const champ = (s: string) =>
  s
    .replace(/[\p{Cc}|]+/gu, ' ')
    .replace(/[’‘]/g, "'")
    .replace(/[—–]/g, '-');
function extraire(du: string, au: string) {
  const dansPeriode = (d: string | null) =>
    d !== null && d.slice(0, 10) >= du && d.slice(0, 10) <= au;
  const pieces = DEMO_FACTURES.filter(
    (f) => ['emise', 'payee', 'annulee'].includes(f.etat) && f.numero && f.devise === 'EUR',
  );
  const ecritures: string[][] = [];
  let nombrePieces = 0;
  let nombreReglements = 0;
  for (const f of pieces.filter((f) => dansPeriode(f.emiseLe))) {
    const numero = f.numero!;
    const jour = date(f.emiseLe!);
    const avoir = f.type === 'avoir';
    const ligne = (compte: { numero: string; libelle: string }, n: number, client: boolean) => {
      const debit = client !== avoir;
      ecritures.push([
        planDemo.journalVentes.code,
        planDemo.journalVentes.libelle,
        numero,
        jour,
        compte.numero,
        compte.libelle,
        client ? `C${f.contactId}` : '',
        client ? (f.contactNom ?? '') : '',
        numero,
        jour,
        `${avoir ? 'Avoir' : 'Facture'} ${numero}`,
        montant(debit ? n : 0),
        montant(debit ? 0 : n),
        '',
        '',
        jour,
        '',
        '',
      ]);
    };
    ligne(planDemo.clients, f.totaux.totalTtcCentimes, true);
    for (const c of f.totaux.parCategorieTva) {
      const marge =
        c.regime === 'marge'
          ? f.lignes
              .filter((l) => l.regimeTva === 'marge')
              .reduce(
                (s, l) =>
                  s +
                  (calculerLigne({
                    designation: l.designation,
                    prixUnitaireCentimes: l.prixUnitaireNetCentimes,
                    quantite: l.quantite,
                    regime: 'marge',
                    prixAchatCentimes: l.prixAchatCentimes ?? 0,
                  }).tvaSurMargeCentimes ?? 0),
                0,
              )
          : 0;
      ligne(planDemo.ventes[c.regime], c.baseCentimes - marge, false);
      if (marge) ligne(planDemo.tva.marge, marge, false);
      if ((c.regime === 'normal' || c.regime === 'reduit_5_5') && c.tvaCentimes)
        ligne(planDemo.tva[c.regime], c.tvaCentimes, false);
    }
    if (f.droitDeSuiteAChargeAcheteur && f.totaux.droitDeSuiteCentimes)
      ligne(planDemo.droitDeSuite, f.totaux.droitDeSuiteCentimes, false);
    nombrePieces++;
  }
  for (const r of DEMO_REGLEMENTS.filter((r) => dansPeriode(r.date) && r.devise === 'EUR')
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id))) {
    const f = pieces.find((f) => f.id === r.factureId);
    if (!f) continue;
    nombreReglements++;
    const jour = date(r.date);
    const numero = `BQ-${jour.slice(0, 4)}-${String(nombreReglements).padStart(6, '0')}`;
    for (const client of [false, true]) {
      const compte = client ? planDemo.clients : planDemo.tresorerie[r.mode];
      const debit = client === (f.type === 'avoir');
      ecritures.push([
        planDemo.journalTresorerie.code,
        planDemo.journalTresorerie.libelle,
        numero,
        jour,
        compte.numero,
        compte.libelle,
        client ? `C${f.contactId}` : '',
        client ? (f.contactNom ?? '') : '',
        f.numero!,
        jour,
        `Règlement ${f.numero}`,
        montant(debit ? r.montantCentimes : 0),
        montant(debit ? 0 : r.montantCentimes),
        '',
        '',
        date(r.creeLe),
        '',
        '',
      ]);
    }
  }
  return { ecritures, nombrePieces, nombreReglements };
}
export function apercuFecDemo(du: string, au: string): ApercuFec {
  const r = extraire(du, au);
  return {
    du,
    au,
    nomFichier: `987654324FEC${date(au)}.txt`,
    pieces: r.nombrePieces,
    reglements: r.nombreReglements,
    lignes: r.ecritures.length,
    exclues: [],
    avertissements: [],
  };
}
export function fichierFecDemo(du: string, au: string): FichierTelecharge {
  const texte =
    [COLONNES, ...extraire(du, au).ecritures].map((l) => l.map(champ).join('\t')).join('\r\n') +
    '\r\n';
  const speciaux: Record<string, number> = {
    '€': 164,
    Œ: 188,
    œ: 189,
    Š: 166,
    š: 168,
    Ž: 180,
    ž: 184,
    Ÿ: 190,
  };
  const octets = Uint8Array.from(
    [...texte].map((c) => speciaux[c] ?? (c.charCodeAt(0) < 256 ? c.charCodeAt(0) : 63)),
  );
  return {
    blob: new Blob([octets], { type: 'text/plain; charset=iso-8859-15' }),
    nomFichier: `987654324FEC${date(au)}.txt`,
  };
}
